<template>
  <v-row>
    <v-col cols="12" md="10">
      <!-- CARD 1: Informacion General del Colegio -->
      <v-card elevation="0" :loading="loading">
        <v-row class="white rounded pa-9">
          <v-row class="mb-3 pa-3">
            <span class="black--text">{{ $t('colleges.general_information') }}</span>
          </v-row>

          <v-col cols="12">
            <v-row class="my-0">
              <v-col cols="2">
                <span class="mr-2">ID</span>
                <span class="deep-purple--text font-weight-bold">{{ school.id }}</span>
              </v-col>
              <v-col cols="4">
                <span class="mr-2">{{ $t('colleges.creation') }}</span>
                <span class="deep-purple--text font-weight-bold">
                  {{ school.creationDate | date({ date: true, hour: false }) }}
                </span>
              </v-col>
              <v-col cols="4">
                <span class="mr-2">{{ $t('colleges.sale') }}</span>
                <span class="deep-purple--text font-weight-bold">{{ paymentByType }}</span>
              </v-col>
              <v-col cols="2">
                <v-btn
                  fab
                  x-small
                  color="deep-purple lighten-1"
                  dark
                  @click="save"
                  :disabled="loading"
                >
                  <v-icon>mdi-content-save</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-divider class="mb-12" />
          </v-col>

          <v-row align="start" justify="center">
            <!-- Columna Izquierda -->
            <v-col cols="12" md="6">
              <v-row align="start" class="d-flex flex-column">
                <span class="body black--text my-1">{{ $t('colleges.school_name') }}</span>
                <v-text-field
                  class="w-80"
                  filled
                  flat
                  dense
                  v-model="school.name"
                />
              </v-row>
              <v-row align="start" class="d-flex flex-column">
                <span class="body black--text my-1">{{ $t('colleges.country') }}</span>
                <v-autocomplete
                  class="w-80 mb-5"
                  filled
                  solo
                  dense
                  :items="countries"
                  item-value="id"
                  item-text="name"
                  v-model="school.baseCountry_id"
                  single-line
                  hide-details
                >
                  <template v-slot:selection="{ item }">
                    <img
                      :src="`${flagsPath}${item.iso_code}.png`"
                      alt="country flag"
                      width="35vh"
                      height="25vh"
                      class="mr-2"
                    />
                    <span class="">{{ item.name }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <template>
                      <img
                        :src="`${flagsPath}${item.iso_code}.png`"
                        alt="country flag"
                        width="35vh"
                        height="25vh"
                        class="mr-2"
                      />
                      <v-list-item-content>
                        <v-list-item-title>{{ item.name | capitalize }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </template>
                </v-autocomplete>
              </v-row>
              <v-row v-if="showState" align="start" class="d-flex flex-column">
                <span class="body black--text my-1">{{ $t('colleges.state_label') }}</span>
                <v-autocomplete
                  class="w-80 mb-5"
                  filled
                  solo
                  dense
                  :items="usStates"
                  v-model="school.state"
                  single-line
                  hide-details
                  clearable
                  @change="onStateChange"
                />
              </v-row>
              <v-row v-if="showTimezone" align="start" class="d-flex flex-column">
                <span class="body black--text my-1">{{ $t('colleges.time_zone_label') }}</span>
                <v-select
                  class="w-80 mb-5"
                  filled
                  solo
                  dense
                  :items="timezoneOptions"
                  item-text="label"
                  item-value="value"
                  v-model="school.time_zone"
                  single-line
                  hide-details
                  clearable
                />
              </v-row>
              <v-row align="start" class="d-flex flex-column">
                <span align="start" class="body black--text my-1">
                  {{ $t('colleges.school_group') }}
                </span>
                <v-select
                  v-model="school.school_group_id"
                  :label="$t('colleges.select_school_group')"
                  :items="schoolGroups"
                  single-line
                  hide-details
                  filled
                  solo
                  dense
                  @input="schoolGroupSelected($event)"
                  class="w-80 mb-5"
                />
              </v-row>
              <v-row align="start" class="d-flex flex-column">
                <span class="body black--text my-1">{{ $t('colleges.commune') }}</span>
                <v-autocomplete
                  class="w-80 mb-5"
                  filled
                  solo
                  dense
                  :items="counties"
                  v-model="school.county_id"
                  single-line
                  hide-details
                />
              </v-row>

              <v-row align="start" class="d-flex flex-column">
                <span class="body black--text my-1">RBD</span>
                <v-text-field class="w-80" filled flat dense v-model="school.rbd" />
              </v-row>

              <v-row align="start" class="d-flex flex-column">
                <span class="body black--text my-1">{{ $t('colleges.payment_identifier') }}</span>
                <v-autocomplete
                  class="w-80 mb-5"
                  filled
                  solo
                  dense
                  :items="formatPaymentByTypeIdentifierOptions"
                  v-model="school.payment_by_type_id"
                  single-line
                  hide-details
                />
              </v-row>
            </v-col>

            <!-- Columna Derecha -->
            <v-col cols="12" md="6">
              <v-row align="start" class="d-flex flex-column">
                <span class="black--text">{{ $t('colleges.status') }}</span>
                <v-radio-group v-model="school.active" row>
                  <v-radio
                    :label="$t('colleges.active')"
                    :value="true"
                    color="deep-purple lighten-1"
                  />
                  <v-radio
                    :label="$t('colleges.inactive')"
                    :value="false"
                    color="deep-purple lighten-1"
                  />
                </v-radio-group>
              </v-row>

              <v-row align="start" class="d-flex flex-column">
                <v-checkbox
                  v-model="school.hide_personal_books"
                  :label="$t('colleges.hide_personal_books')"
                  color="deep-purple lighten-1"
                  hide-details
                />
              </v-row>

              <v-row align="start" class="d-flex flex-column">
                <v-checkbox
                  v-model="school.only_allow_sso"
                  :label="$t('colleges.only_allow_sso')"
                  color="deep-purple lighten-1"
                  hide-details
                />
              </v-row>

              <v-row align="start" class="d-flex flex-column">
                <v-checkbox
                  v-model="school.disable_autonomous_fluency"
                  :label="$t('colleges.disable_autonomous_fluency')"
                  color="deep-purple lighten-1"
                  hide-details
                />
              </v-row>

              <v-row align="start" class="d-flex flex-column mt-4">
                <span align="start" class="body black--text my-1">
                  {{ $t('colleges.calendar_type') }}
                </span>
                <v-select
                  v-model="school.calendar_type_id"
                  :label="$t('colleges.select_calendar_type')"
                  :items="calendarTypes"
                  item-value="id"
                  item-text="name"
                  single-line
                  hide-details
                  filled
                  solo
                  dense
                  @change="onCalendarTypeChange"
                  class="w-80 mb-5"
                />
              </v-row>

              <v-row align="center" justify="start" class="d-flex flex-column mt-8 mb-4">
                <span class="body black--text my-1">
                  {{ $t('colleges.current_period_of_operation') }}
                </span>
                <div>
                  <span class="deep-purple--text lighten-1 font-weight-bold body-1">
                    {{ school.operationYear}}
                  </span>
                  <v-btn text small class="ma-0 pa-0" @click="migrate">
                    <span class="black--text font-weight-bold text-decoration-underline">
                      {{ $t('colleges.migrate_period') }}
                    </span>
                  </v-btn>
                </div>
              </v-row>
            </v-col>
          </v-row>

          <v-col cols="12">
            <v-divider />
            <v-row class="pa-3">
              <v-col cols="12" md="2">
                <v-icon size="60" color="deep-purple lighten-1">mdi-contacts</v-icon>
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-start flex-column">
                <span>{{ $t('colleges.contact') }}</span>
                <v-text-field
                  class="ma-0 pa-0 w-80"
                  filled
                  flat
                  dense
                  v-model="school.contact"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-start flex-column">
                <span>{{ $t('colleges.phone') }}</span>
                <v-text-field class="w-80" filled flat dense v-model="school.phone" hide-details />
              </v-col>
            </v-row>
          </v-col>

        </v-row>
      </v-card>

      <!-- CARD 2: Historial de Acuerdos -->
      <v-card v-if="!loading" elevation="0" class="mt-6">
        <v-row class="white rounded pa-9">
          <!-- Caso: Colegio SIN conglomerado -->
          <template v-if="!readOnly">
            <v-col cols="12">
              <v-row align="start">
                <v-col cols="12" md="6">
                  <h3 class="mb-4 text-left">{{ $t('colleges.agreement_history') }}</h3>
                </v-col>
                <v-col cols="12" md="6" class="text-right">
                   <v-btn
                    text
                    small
                    color="deep-purple lighten-1"
                    :disabled="!school.id"
                    @click="toggleCreateArrangementModal"
                  >
                    <v-icon size="21" class="mr-1">mdi-plus</v-icon>
                    {{ $t('conglomerates.create_agreement') }}
                  </v-btn>
                </v-col>
              </v-row>

              <conglomerate-arrangements-card
                :arrangements="arrangements"
                :loading="loadingArrangements"
                @create="toggleCreateArrangementModal"
                @edit="handleEditArrangement"
              />
            </v-col>
          </template>

          <!-- Caso: Colegio CON conglomerado (hereda acuerdo) -->
          <template v-else>
            <v-col cols="12">
              <v-row align="start">
                <v-col cols="12">
                  <h3 class="mb-4">{{ $t('colleges.inherited_agreement') }}</h3>
                </v-col>
              </v-row>

              <v-alert type="info" text dense class="mb-4">
                {{ $t('colleges.agreement_managed_by_conglomerate') }}
                <router-link :to="`/edit/conglomerates/${school.school_group_id}`">
                  {{ $t('colleges.view_conglomerate_agreement') }}
                </router-link>
              </v-alert>

              <conglomerate-arrangements-card
                :arrangements="inheritedArrangement"
                :loading="loadingArrangements"
                :readonly="true"
              />
            </v-col>
          </template>
        </v-row>
      </v-card>

      <!-- CARD 3: Periodos Academicos -->
      <v-card v-if="!loading" elevation="0" class="mt-6">
        <v-row class="white rounded pa-9">
          <v-col cols="12">
            <v-row class="mb-3 align-start">
              <v-col cols="8" class="text-left">
                <span class="black--text text-h6">
                  {{ $t('colleges.academic_periods') }}
                </span>
              </v-col>
              <v-col cols="4" class="text-right">
                <v-btn
                  color="primary"
                  @click="showCreatePeriodModal = true"
                  :disabled="!school.id"
                >
                  <v-icon left>mdi-plus</v-icon>
                  {{ $t('colleges.create_period') }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <academic-periods-data-table
                  :periods="academicPeriods"
                  :loading="loadingPeriods"
                  @edit-period="editPeriod"
                  @delete-period="deletePeriod"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card>
    </v-col>

    <!-- Modals de Periodos Academicos -->
    <edit-academic-period-modal
      v-model="showEditPeriodModal"
      :period="selectedPeriod"
      :calendar-type="selectedCalendarType"
      :periods="academicPeriods"
      @period-updated="onPeriodUpdated"
    />

    <create-academic-period-modal
      v-model="showCreatePeriodModal"
      :school-id="school.id"
      :periods="academicPeriods"
      @period-created="onPeriodCreated"
    />

    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">{{ $t('colleges.delete_period') }}</v-card-title>
        <v-card-text>
          {{ $t('colleges.confirm_delete_period') }}
          <br />
          <strong>{{ periodToDelete ? periodToDelete.name : '' }}</strong>
          <br /><br />
          <v-alert type="warning" dense>
            {{ $t('colleges.delete_period_warning') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="error" @click="confirmDeletePeriod" :loading="loading">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modals de Acuerdos (solo si no es readOnly) -->
    <create-arrangement-modal
      v-if="!readOnly"
      v-model="isCreateArrangementModalOpen"
      :school-id="school.id"
      @created="loadArrangements"
    />

    <edit-arrangement-modal
      v-if="!readOnly"
      v-model="isEditArrangementModalOpen"
      :arrangement="selectedArrangement"
      @updated="loadArrangements"
    />
  </v-row>
</template>

<script>
import { mapState } from 'vuex';

import { FLAGS_URL, DEFAULT_SCHOOL_DETAIL, PAYMENT_BY_TYPE } from '@/constants/data';
import { formatPaymentByTypeIdentifierOptions } from '@/constants/data/FormatInputMassiveLoad.json';
import {
  US_STATES, STATE_TO_TZ, US_TIMEZONES, MX_TIMEZONES,
} from '@/constants/locations';
import LoadingMixin from '@/mixins/loading';
import SnackbarMixin from '@/mixins/snackbar';
import countryService from '@/services/country.service';
import countyService from '@/services/county.service';
import schoolService from '@/services/school.service';
import schoolGroupService from '@/services/schoolGroup.service';
import calendarTypeService from '@/services/calendarType.service';
import academicPeriodService from '@/services/academicPeriod.service';

import schoolArrangementService from '@/services/schoolArrangement.service';
import AcademicPeriodsDataTable from '@/components/colleges/academicPeriods/AcademicPeriodsDataTable.vue';
import EditAcademicPeriodModal from '@/components/colleges/academicPeriods/EditAcademicPeriodModal.vue';
import CreateAcademicPeriodModal from '@/components/colleges/academicPeriods/CreateAcademicPeriodModal.vue';
import ConglomerateArrangementsCard from '@/components/conglomerates/ConglomerateArrangementsCard.vue';
import CreateArrangementModal from '@/components/conglomerates/modals/CreateArrangementModal.vue';
import EditArrangementModal from '@/components/conglomerates/modals/EditArrangementModal.vue';

export default {
  name: 'CollegeEdition',
  components: {
    AcademicPeriodsDataTable,
    EditAcademicPeriodModal,
    CreateAcademicPeriodModal,
    ConglomerateArrangementsCard,
    CreateArrangementModal,
    EditArrangementModal,
  },
  data: () => ({
    flagsPath: `${FLAGS_URL}countries_flags/`,
    countries: [],
    counties: [],
    school: { ...DEFAULT_SCHOOL_DETAIL },
    formatPaymentByTypeIdentifierOptions,
    schoolGroups: [],
    calendarTypes: [],
    arrangements: [],
    inheritedArrangement: [],
    loadingArrangements: false,
    isCreateArrangementModalOpen: false,
    isEditArrangementModalOpen: false,
    selectedArrangement: null,
    academicPeriods: [],
    loadingPeriods: false,
    showEditPeriodModal: false,
    showCreatePeriodModal: false,
    showDeleteDialog: false,
    selectedPeriod: null,
    periodToDelete: null,
  }),
  mixins: [LoadingMixin, SnackbarMixin],
  computed: {
    ...mapState({
      currentUser: ({ currentUser }) => currentUser,
    }),
    readOnly() {
      return !!this.school.school_group_id;
    },
    selectedCalendarType() {
      if (!this.school.calendar_type_id || !this.calendarTypes.length) {
        return null;
      }
      return this.calendarTypes.find((ct) => ct.id === this.school.calendar_type_id) || null;
    },
    paymentByType() {
      return PAYMENT_BY_TYPE.find((item) => item.id === this.school.payment_by_type_id)?.name ?? '';
    },
    schoolCountryIso() {
      // Derive from the reactive baseCountry_id first so in-screen country
      // changes re-evaluate; the nested school.country is only a fallback and
      // is never updated by the country selector.
      const fromList = this.countries.find((c) => c.id === this.school.baseCountry_id)?.iso_code;
      return fromList ?? this.school.country?.iso_code ?? null;
    },
    showState() {
      return this.schoolCountryIso === 'USA';
    },
    showTimezone() {
      return this.schoolCountryIso === 'USA' || this.schoolCountryIso === 'MEX';
    },
    timezoneOptions() {
      return this.schoolCountryIso === 'MEX' ? MX_TIMEZONES : US_TIMEZONES;
    },
    usStates() {
      return US_STATES;
    },
  },
  async mounted() {
    this.loader();
    try {
      const [countriesRes, schoolRes, calendarTypesRes] = await Promise.all([
        countryService.findAll(),
        schoolService.findOne(this.$route.params.collegeId),
        calendarTypeService.findAll(),
      ]);

      if (countriesRes?.data) this.countries = countriesRes.data;
      if (schoolRes?.data) {
        this.school = schoolRes.data;
        // Guarantee state/time_zone are reactive without widening the update
        // payload with other defaults (Vue 2 can't track keys the response omits).
        this.$set(this.school, 'state', this.school.state ?? null);
        this.$set(this.school, 'time_zone', this.school.time_zone ?? null);
      }
      if (calendarTypesRes?.data) this.calendarTypes = calendarTypesRes.data;

      await this.getSchoolGroups();
      await this.loadArrangements();
      await this.loadAcademicPeriods();
    } catch {
      this.snackbarError(this.$t('colleges.error_edition_msg'));
    } finally {
      this.loader();
    }
  },
  methods: {
    migrate() {
      // TODO: Implement migration logic
    },
    async save() {
      this.loader();
      try {
        const result = await schoolService.update(this.school);
        if (result?.status === 200) {
          this.snackbarSuccess(this.$t('colleges.success_edition_msg'));
        } else {
          this.snackbarError(this.$t('colleges.error_edition_msg'));
        }
      } catch {
        this.snackbarError(this.$t('colleges.error_edition_msg'));
      } finally {
        this.loader();
      }
    },

    onStateChange(state) {
      this.school.time_zone = state ? (STATE_TO_TZ[state] ?? null) : null;
    },

    async loadArrangements() {
      if (this.readOnly) {
        await this.loadInheritedArrangement();
        return;
      }
      if (!this.school.id) return;
      await this.fetchArrangements(
        () => schoolArrangementService.findAllBySchoolId(this.school.id),
        (content) => { this.arrangements = content; },
      );
    },
    async loadInheritedArrangement() {
      if (!this.school.school_group_id) return;
      await this.fetchArrangements(
        () => schoolArrangementService.findAllBySchoolGroupId(this.school.school_group_id),
        (content) => { this.inheritedArrangement = content; },
      );
    },
    async fetchArrangements(fetcher, onSuccess) {
      this.loadingArrangements = true;
      try {
        const res = await fetcher();
        if (res?.status === 'OK') {
          onSuccess(res.content || []);
        } else {
          onSuccess([]);
          this.snackbarError(this.$t('colleges.error_edition_msg'));
        }
      } catch {
        onSuccess([]);
        this.snackbarError(this.$t('colleges.error_edition_msg'));
      } finally {
        this.loadingArrangements = false;
      }
    },
    async getSchoolGroups() {
      try {
        this.schoolGroups = await schoolGroupService.findList();
      } catch (error) {
        this.snackbarError(error.message);
      }
    },
    async schoolGroupSelected() {
      await this.loadArrangements();
    },
    async loadAcademicPeriods() {
      if (!this.school.id) return;

      this.loadingPeriods = true;
      try {
        const response = await academicPeriodService.findAllBySchool(this.school.id);
        if (response.status === 200) {
          this.academicPeriods = response.data || [];
        }
      } catch (error) {
        this.snackbarError(this.$t('colleges.period_error'));
      } finally {
        this.loadingPeriods = false;
      }
    },
    async onCalendarTypeChange(calendarTypeId) {
      if (!calendarTypeId) return;

      try {
        // Find current default period
        const currentPeriod = this.academicPeriods.find(
          (p) => p.calendarTypeId === this.school.calendar_type_id,
        );

        if (currentPeriod) {
          // Get the new calendar type details
          const newCalendarType = this.calendarTypes.find((ct) => ct.id === calendarTypeId);
          if (newCalendarType) {
            // Update current period with new calendar type dates
            const currentYear = new Date().getFullYear();
            const startDate = new Date(
              currentYear,
              newCalendarType.startMonth - 1,
              newCalendarType.startDay,
            );
            const endDate = new Date(
              currentYear,
              newCalendarType.endMonth - 1,
              newCalendarType.endDay,
            );

            // If end date is before start date, it means it's in the next year
            if (endDate < startDate) {
              endDate.setFullYear(currentYear + 1);
            }

            await academicPeriodService.update(currentPeriod.id, {
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0],
            });

            await this.loadAcademicPeriods();
          }
        }

        await this.save();
      } catch (error) {
        this.snackbarError(this.$t('colleges.period_error'));
      }
    },
    editPeriod(period) {
      this.selectedPeriod = period;
      this.showEditPeriodModal = true;
    },
    deletePeriod(period) {
      this.periodToDelete = period;
      this.showDeleteDialog = true;
    },
    async confirmDeletePeriod() {
      if (!this.periodToDelete) return;

      this.loader();
      try {
        const response = await academicPeriodService.delete(this.periodToDelete.id);
        if (response.status === 200 || response.status === 204) {
          this.snackbarSuccess(this.$t('colleges.period_deleted_success'));
          await this.loadAcademicPeriods();
        }
      } catch (error) {
        this.snackbarError(this.$t('colleges.period_error'));
      } finally {
        this.loader();
        this.showDeleteDialog = false;
        this.periodToDelete = null;
      }
    },
    async onPeriodUpdated() {
      await this.loadAcademicPeriods();
      this.showEditPeriodModal = false;
    },
    async onPeriodCreated() {
      await this.loadAcademicPeriods();
      this.showCreatePeriodModal = false;
    },
    toggleCreateArrangementModal() {
      this.isCreateArrangementModalOpen = !this.isCreateArrangementModalOpen;
    },
    handleEditArrangement(arrangement) {
      this.selectedArrangement = arrangement;
      this.isEditArrangementModalOpen = true;
    },

  },
  watch: {
    'school.baseCountry_id': async function onCountryChange(countryId, oldCountryId) {
      // Clear state/time_zone on a GENUINE country change only. This watcher also
      // fires during initial hydration (findOne replaces school, so baseCountry_id
      // goes from its default 0 to the real id); clearing then would wipe the just
      // preloaded state/time_zone. The default is 0 (not null), so guard on
      // truthiness: real country ids are 1..34, so oldCountryId is falsy only on
      // the hydration transition. showTimezone is true for both USA and MEX, so a
      // USA<->MEX switch must clear the TZ from the other country's disjoint set.
      if (oldCountryId && oldCountryId !== countryId) {
        this.school.state = null;
        this.school.time_zone = null;
      }
      if (!countryId) {
        return;
      }
      this.loader();
      const response = await countyService.findListByCountryId(countryId)
        .catch(() => undefined)
        .finally(() => { this.loader(); this.school.county_id = null; });

      if (response && response.data) {
        this.counties = response.data;
      } else {
        this.counties = [];
      }
    },
  },
};
</script>

<style scoped>
.w-80 {
  width: 80%;
}
</style>
